import { type FC, type PropsWithChildren } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { Divider, FormControl, FormHelperText, Grid, Stack, TextField } from '@mui/material';

import { BACKGROUND_FIELD_DESCRIPTIONS } from '#apps/client-web/features/audit-form-background/constants';
import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import { useFormDescriptions } from '#apps/client-web/features/settings/hooks/use-form-descriptions';

import {
  SubFormDialog,
  type SubFormDialogProps,
} from '#packages/web/layouts/dialog/templates/sub-form';

export type UpgradesFormDialogProps = Pick<SubFormDialogProps, 'onClose' | 'open'> & {
  control: Control<EditAuditFormBackgroundData>;
};

export const UpgradesFormDialog: FC<PropsWithChildren<UpgradesFormDialogProps>> = ({
  open,
  control,
  onClose,
}) => {
  const getDescription = useFormDescriptions(BACKGROUND_FIELD_DESCRIPTIONS);

  return (
  <SubFormDialog title="Upgrades" open={open} onClose={onClose}>
    <Grid container spacing={2}>
      <Controller
        control={control}
        name="general.recentUpgrades"
        render={({ field, fieldState }) => (
          <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
            <TextField
              {...field}
              label="Recent upgrades"
              type="text"
              variant="outlined"
              multiline
              rows={4}
              {...getFormErrorAttributes(fieldState.error)}
            />
            <FormHelperText>{fieldState.error?.message ?? getDescription(field.name)}</FormHelperText>
          </FormControl>
        )}
      />

      <Stack sx={{ width: '100%', px: 1 }}>
        <Divider />
      </Stack>

      <Controller
        control={control}
        name="general.noGoProjects"
        render={({ field, fieldState }) => (
          <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
            <TextField
              {...field}
              label="No-go projects"
              type="text"
              variant="outlined"
              multiline
              rows={4}
              {...getFormErrorAttributes(fieldState.error)}
            />
            <FormHelperText>{fieldState.error?.message}</FormHelperText>
          </FormControl>
        )}
      />

      <Stack sx={{ width: '100%', px: 1 }}>
        <Divider />
      </Stack>

      <Controller
        control={control}
        name="general.plansForUpgrades"
        render={({ field, fieldState }) => (
          <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
            <TextField
              {...field}
              label="Plans for upgrades"
              type="text"
              variant="outlined"
              multiline
              rows={4}
              {...getFormErrorAttributes(fieldState.error)}
            />
            <FormHelperText>{fieldState.error?.message ?? getDescription(field.name)}</FormHelperText>
          </FormControl>
        )}
      />

      <Stack sx={{ width: '100%', px: 1 }}>
        <Divider />
      </Stack>

      <Controller
        control={control}
        name="general.energyOpportunities"
        render={({ field, fieldState }) => (
          <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
            <TextField
              {...field}
              label="Energy opportunities"
              type="text"
              variant="outlined"
              multiline
              rows={4}
              {...getFormErrorAttributes(fieldState.error)}
            />
            <FormHelperText>{fieldState.error?.message}</FormHelperText>
          </FormControl>
        )}
      />
    </Grid>
  </SubFormDialog>
  );
};
